/**
 * The payment page a member lands on from a WhatsApp link.
 *
 * Public — no sign-in. It arrives on a phone, from someone who may not have
 * opened the app before, and it has one job: make it obvious what the money
 * buys, then take it.
 *
 * The through-line is the bib. Every member of this club IS a number, and an
 * unpaid membership is a bib that has not been activated yet — so the page
 * opens with their bib greyed out and ends with it lit. Paying is not filling
 * in a form; it is switching your number back on.
 *
 * The club asks for two things, and the page says both. Money is one. Eight
 * volunteer hours a year is the other, and it is stated here rather than
 * discovered later — a member who finds out about it afterwards feels
 * ambushed, and the volunteer desk is the hardest part of the club to staff.
 *
 * The URL carries a one-time TOKEN, not a member id, and the lookup happens
 * server-side (`GET /renew/:token`). It has to: a public page that resolves a
 * member id would let anyone walk the ids and read names, bibs and dues state
 * straight off the roster. The token is 256 bits, stored only as its hash, and
 * it dies when the payment lands.
 *
 * So this page holds no club. It fetches the little it needs — a name, a bib,
 * a tier, what is owed — and pays through the same `applyPayment` transaction
 * the committee's manual entry uses, so the receipt number and the new expiry
 * cannot come out different depending on who pressed the button.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  bibNo,
  CLUB,
  DISTS,
  duesState,
  EXPECT_VOL,
  formatDate,
  formatMoney,
  payable,
  TIERS,
} from '../data';
import { api } from '../lib/api';
import { previewCoversTill } from '../context/ClubContext';
import { usePublicGrounds } from '../hooks/usePublicGrounds';
import { PaymentPanel } from '../components/PaymentPanel';

/* The ask panel: white card, top rule coloured by state (brand = pay now,
   emerald = settled). */
const PANEL = 'border border-line border-t-[3px] bg-white px-5 py-[22px] text-center';
const PANEL_H = 'font-market text-[26px] font-extrabold leading-[1.05] tracking-[-0.03em]';
const PANEL_P = 'mt-2 text-[13.5px] leading-relaxed text-copy-muted';
const FOOT = 'mt-7 text-center font-market text-[10px] leading-[1.8] text-copy-label';

/* bibClass() still speaks the old vocabulary ('t5'…'t42' or '') — map it to
   the Tailwind distance-ramp utilities. */
const BIB_TOP = {
  t5: 'border-t-d5',
  t10: 'border-t-d10',
  t21: 'border-t-d21',
  t42: 'border-t-d42',
  '': 'border-t-line',
};

/** What the fee actually buys. Concrete, not adjectives. */
// [title, detail] pairs
const COVERS = [
  ['Group runs, every morning', '6:00–8:00 AM at Nanjappa, Chikanna and the Collector Office'],
  ['Your bib number', 'Yours for life. Every race time is recorded against it'],
  ['Club races', 'Entry to the Tirupur Runners Marathon and club trips to Chennai and Coimbatore'],
  ['Pace groups and coaching', 'Sunday long runs are paced, from first-timers to sub-2 half'],
  ['Your safety details on file', 'Emergency contact and blood group, held by the committee only'],
];

function Check() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

export function PayPage() {
  const grounds = usePublicGrounds();
  const { token } = useParams();

  /** What the link resolves to, or the reason it does not. */
  const [member, setMember] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  /** The payment screenshot, in manual (QR / bank) mode. Optional. */
  const [proof, setProof] = useState(null);

  /**
   * What the member just bought, captured at the moment of payment.
   *
   * It cannot be recomputed afterwards: once the payment lands, their
   * duesPaidTill is already in the future, so asking "what would a payment
   * cover?" a second time quotes a second year.
   */
  // { receipt, coversTill } once paid, otherwise null
  const [paid, setPaid] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .openRenewalLink(token)
      .then((data) => {
        if (!cancelled) setMember(data);
      })
      .catch((err) => {
        if (!cancelled) setLinkError(err?.message ?? 'This payment link is not valid.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading)
    return (
      <div className="flex h-[60vh] items-center justify-center font-market text-xs text-copy-muted">
        Loading…
      </div>
    );

  if (!member) {
    return (
      <PayShell>
        <div className={`${PANEL} border-t-gold-500`}>
          <div className={PANEL_H}>This link has expired</div>
          <p className={PANEL_P}>
            {linkError ?? 'Ask the committee to send you a new one.'} Nothing has been charged.
          </p>
          {/* These arrive from WhatsApp with no history behind them, and the
              recipient is an existing member — leaving them on a terminal card
              with no control was a dead end. */}
          <Link
            to="/signin"
            className="mt-4 inline-block font-market text-xs uppercase tracking-wide text-gold-800 underline underline-offset-2"
          >
            Sign in to the club →
          </Link>
        </div>
      </PayShell>
    );
  }

  const tier = TIERS[member.tier];
  const dues = duesState(member);
  const coversTill = previewCoversTill(member);
  const hours = member.volunteerHours ?? 0;

  /* ---- already paid ---- */
  if (!paid && !payable(member)) {
    return (
      <PayShell>
        <div className="mb-4">
          <PayBib member={member} active />
        </div>
        <div className={`${PANEL} border-t-sea-600`}>
          <div className={PANEL_H}>Nothing to pay</div>
          <p className={PANEL_P}>
            {member.tier === 'life'
              ? 'You are a life member of the club. There is no renewal, ever.'
              : `Your membership runs to ${formatDate(member.duesPaidTill)}. We will send a link when it is time to renew.`}
          </p>
        </div>
        <VolunteerAsk hours={hours} />
      </PayShell>
    );
  }

  /* ---- paid, just now ---- */
  if (paid) {
    return (
      <PayShell>
        <div className="mb-4">
          <PayBib member={member} active />
        </div>
        <div className={`${PANEL} border-t-sea-600`}>
          <div className={PANEL_H}>Bib {bibNo(member.bib)} is active</div>
          <p className={PANEL_P}>
            Paid {formatMoney(member.amount)}. Your membership runs to{' '}
            <b>{formatDate(paid.coversTill)}</b>.
          </p>
          <div className="mt-4 flex items-baseline justify-between bg-line-chip px-3.5 py-3 font-market text-[11px] text-copy-muted [&_b]:text-[12.5px] [&_b]:text-ink-700">
            <span>Receipt</span>
            <b>{paid.receipt}</b>
          </div>
        </div>
        <VolunteerAsk hours={hours} />
        <p className={FOOT}>A receipt has gone to your mobile. See you at the grounds.</p>
      </PayShell>
    );
  }

  /* ---- payment due ---- */
  // How the club collects decides what this page DOES: 'razorpay' pays
  // instantly through the gateway; 'manual' means "pay the QR / account
  // below, attach your screenshot, then confirm".
  const manual = member.payMode !== 'razorpay';

  async function pay() {
    if (!member) return;
    setPaying(true);
    setError(null);
    try {
      // The receipt number and the new expiry are both the server's answer,
      // from the same transaction the committee's manual entry uses.
      const result = await api.payRenewalLink(token, manual ? proof : null);
      setPaid({ receipt: result.receipt, coversTill: result.coversTill });
      window.scrollTo(0, 0);
    } catch (err) {
      setError(
        err?.message ??
          'That payment did not go through. Nothing has been charged — please try again.',
      );
      setPaying(false);
    }
  }

  const isLapsed = dues.key === 'lapsed';

  /*
   * A checkout, not a leaflet: what is owed and how to pay it sit side by side
   * at the top, and the membership blurb moves to the column beside them
   * (owner's direction, 2 Sep 2026). It used to run amount → blurb → volunteer
   * ask → bank details, which put the account number three scrolls below the
   * decision to pay it.
   */
  return (
    <PayShell wide>
      <div className="grid items-start gap-6 lg:h-full lg:min-h-0 lg:grid-cols-[1fr_388px] lg:gap-8">
        {/* ---- left: who this is, and what the money buys. Scrolls on its
               own above lg so the payment desk beside it stays put. ---- */}
        <div className="min-w-0 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:pb-6 lg:pr-2">
          <PayBib member={member} active={false} />
          <div className="mt-3 text-center font-market text-[10.5px] text-copy-muted lg:text-left">
            {isLapsed
              ? `Lapsed ${formatDate(member.duesPaidTill)} — pay to switch it back on`
              : dues.key === 'pending'
                ? 'Not active yet — pay to switch it on'
                : `Runs out ${formatDate(member.duesPaidTill)} — renew to keep it`}
          </div>

          <div className="mt-6 border border-line bg-white px-5 py-[18px]">
            <div className="font-market text-[9.5px] uppercase tracking-[0.16em] text-copy-label">
              {tier.label} membership
            </div>
            <div className="mt-1 font-market text-[15px] font-bold tracking-[-0.02em] text-ink-700">
              {formatMoney(member.amount)} · {tier.months} months
              {coversTill && (
                <span className="font-normal text-copy-muted">
                  {' '}
                  → {formatDate(coversTill)}
                </span>
              )}
            </div>
          </div>

          <section className="mt-6">
            <h2 className="mb-3 font-market text-[19px] font-extrabold tracking-[-0.025em]">
              What your membership covers
            </h2>
            <ul className="list-none">
              {COVERS.map(([title, detail]) => (
                <li key={title} className="flex gap-3 border-b border-line py-3 last:border-b-0">
                  <span className="mt-px grid h-[21px] w-[21px] flex-none place-items-center rounded-full bg-sea-50 text-sea-600">
                    <Check />
                  </span>
                  <div>
                    <b className="block text-[13.5px] font-semibold">{title}</b>
                    <span className="mt-0.5 block text-[12.5px] leading-relaxed text-copy-muted">
                      {detail}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <VolunteerAsk hours={hours} />

          <p className={FOOT}>
            {CLUB.name} · est. {CLUB.established} · {grounds[member.ground] ?? CLUB.city}
            <br />
            Questions about your membership? Reply to the WhatsApp message.
          </p>
        </div>

        {/* ---- right: the payment desk. Sticky on a desktop so the action
               stays put while the left column is read. ---- */}
        <div className="lg:h-full lg:min-h-0 lg:overflow-y-auto lg:pb-6">
          <PaymentPanel
            amount={member.amount}
            payMode={member.payMode}
            upi={member.bankUpi}
            qrUrl={member.bankQrUrl}
            holder={member.bankHolder}
            account={member.bankAccount}
            ifsc={member.bankIfsc}
            details={member.bankDetails}
            bib={member.bib ? bibNo(member.bib) : null}
            proof={proof}
            onProofChange={setProof}
            onPay={() => void pay()}
            paying={paying}
            error={error}
            coversTill={coversTill ? formatDate(coversTill) : null}
          />
        </div>
      </div>
    </PayShell>
  );
}

/**
 * The other half of membership.
 *
 * Deliberately given the same weight as the amount due, and in volunteer
 * emerald rather than amber — this is not an action to take today, it is the
 * standing expectation of being in this club.
 */
function VolunteerAsk({ hours }) {
  const done = hours >= EXPECT_VOL;
  return (
    <section className="mt-7 border border-sea-300 bg-sea-50 px-5 py-[22px]">
      <div className="font-market text-[9.5px] uppercase tracking-[0.16em] text-sea-600">
        The club also asks for your time
      </div>
      <div className="mb-3 mt-2 font-market text-[40px] font-extrabold leading-none">
        {EXPECT_VOL} hours a year
        <small className="ml-2 font-market text-[11px] font-normal tracking-wide text-copy-muted">
          per member
        </small>
      </div>
      <p className="mb-2 text-[13px] leading-[1.7] text-copy-muted">
        Membership is not only running. A club of 250 puts on a marathon for thousands, and it
        works because members staff it — aid stations, route marshalling, the bib desk, medical
        support, photography, setup the evening before.
      </p>
      <p className="mb-2 text-[13px] leading-[1.7] text-copy-muted">
        You pick the work that suits you from inside any event, and your hours are logged against
        your bib.
      </p>
      <div className="mt-4 h-[7px] overflow-hidden rounded-full bg-white">
        <i
          className="block h-full rounded-full bg-sea-600"
          style={{ width: `${Math.min(100, (hours / EXPECT_VOL) * 100)}%` }}
        />
      </div>
      <div className="mt-2 font-market text-[10px] text-copy-muted">
        {done
          ? `${hours} of ${EXPECT_VOL} hours this year — target met. Thank you.`
          : `${hours} of ${EXPECT_VOL} hours this year`}
      </div>
    </section>
  );
}

/** The bib, lit or unlit. Unpaid renders desaturated and set back, so the lit
 *  version reads as a change of state rather than a change of screen. */
function PayBib({ member, active }) {
  const grounds = usePublicGrounds();
  /*
   * The bib's top rule is the colour of their longest completed distance. This
   * page has no result set to derive it from, so the API sends the distance
   * itself — the same value `stats.longestDistance` holds, recomputed
   * server-side from every result the member has.
   */
  const cls = DISTS.find((d) => d.key === member.longestDistance)?.cls ?? '';
  const top = active ? (BIB_TOP[cls] ?? BIB_TOP['']) : BIB_TOP[''];
  return (
    <div
      className={`flex overflow-hidden border border-line bg-white transition-all duration-500 ${
        active ? '' : 'opacity-60 saturate-[0.15]'
      }`}
    >
      <div
        className="flex w-[17px] flex-none flex-col items-center justify-around border-r border-dashed border-line-strong bg-ground py-2.5"
        aria-hidden="true"
      >
        {Array.from({ length: 6 }, (_, i) => (
          <i key={i} className="h-[3px] w-[3px] rounded-full bg-line-strong" />
        ))}
      </div>
      <div className={`min-w-0 flex-1 border-t-[3px] p-5 ${top}`}>
        {/* A bib is allocated at activation, so a member paying to join has
            none yet — say that, rather than print a 58px placeholder. */}
        {member.bib == null ? (
          <div className="font-market text-[15px] font-extrabold uppercase leading-none tracking-[0.16em] text-copy-label">
            Bib on activation
          </div>
        ) : (
          <div className="font-market text-[58px] font-extrabold leading-[0.8] tabular-nums">
            {bibNo(member.bib)}
          </div>
        )}
        <div className="mt-2 truncate font-market text-[23px] font-semibold tracking-[-0.025em]">
          {member.name}
        </div>
        <div className="mt-2 font-market text-[10px] text-copy-muted">
          {TIERS[member.tier].label} · {grounds[member.ground] ?? '—'}
        </div>
      </div>
    </div>
  );
}

/**
 * `wide` is the two-column checkout. Above `lg` it becomes a FIXED-HEIGHT
 * frame: the page itself does not scroll, each column does (owner's report,
 * 2 Sep 2026 — the browser scrollbar moved the payment panel off screen just
 * as someone was reading the account number off it). Below `lg` there is not
 * enough height to divide, so it falls back to ordinary page scrolling.
 */
function PayShell({ children, wide = false }) {
  return (
    <div
      className={`min-h-[100dvh] bg-ground ${
        wide ? 'lg:flex lg:h-[100dvh] lg:min-h-0 lg:flex-col lg:overflow-hidden' : ''
      }`}
    >
      <header className="flex-none bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_52%,#0A0E19_100%)] px-[18px] py-4">
        <div className="font-market text-lg font-extrabold leading-none tracking-[-0.025em] text-white">
          Tirupur<span className="text-gold-400">·</span>Runners
          <small className="mt-1 block font-market text-[8.5px] font-normal normal-case tracking-[0.16em] text-copy-label">
            MEMBERSHIP
          </small>
        </div>
      </header>
      <main
        className={`mx-auto w-full px-4 pt-[22px] ${
          wide
            ? 'max-w-[880px] pb-6 lg:min-h-0 lg:flex-1 lg:pb-0'
            : 'max-w-[460px] pb-14'
        }`}
      >
        {children}
      </main>
    </div>
  );
}
